/**
 * <s-variant-gallery> — Brings the selected variant's photo into view as soon as a buyer
 * picks a colour (or any option), using the variant images assigned in Shopify.
 *
 * Required child markup:
 *   script[type="application/json"][data-variant-media] — { "<variantId>": "<mediaId>" | null }
 *   [data-product-gallery]                              — The gallery track (one-photo carousel on every screen).
 *   [data-gallery-dot]                                  — Optional dots under the gallery, holding each photo index.
 *   [data-media-id]                                     — One per gallery item, carrying the media id.
 *
 * Behaviour:
 *   - Listens for `change` events bubbling from option inputs that carry data-variant-id
 *     (radio pills, swatches, or <select> options), so it reacts instantly, before the
 *     product form partial is refreshed.
 *   - Phones: scrolls the horizontal gallery to the matching photo.
 *   - Desktop: scrolls the page so the matching photo is in view if it is not, and gives it a
 *     brief soft highlight.
 *   - On load with ?variant=, the phone gallery opens on that variant's photo.
 *   - Respects prefers-reduced-motion.
 */
class VariantGallery extends HTMLElement {
  #ac;
  #map = {};
  #highlightTimer;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const payload = this.querySelector('script[type="application/json"][data-variant-media]');
    try {
      this.#map = payload ? JSON.parse(payload.textContent || '{}') : {};
    } catch {
      this.#map = {};
    }

    this.addEventListener('change', event => this.#onChange(event), opts);

    // Gallery dots: the active dot follows the photo in view; tapping a dot opens its photo.
    const gallery = this.querySelector('[data-product-gallery]');
    const dots = [...this.querySelectorAll('[data-gallery-dot]')];
    if (gallery && dots.length) {
      let frame = 0;
      gallery.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => this.#syncDots(gallery, dots));
        },
        { passive: true, signal: this.#ac.signal }
      );
      this.addEventListener(
        'click',
        event => {
          const step = event.target instanceof Element ? event.target.closest('[data-gallery-step]') : null;
          if (step) {
            const items = gallery.querySelectorAll('[data-media-id]');
            const current = Math.round(gallery.scrollLeft / (gallery.clientWidth || 1));
            const next = (current + Number(step.getAttribute('data-gallery-step')) + items.length) % items.length;
            if (items[next]) this.#scrollTo(gallery, items[next], false);
            return;
          }
          const dot = event.target instanceof Element ? event.target.closest('[data-gallery-dot]') : null;
          if (!dot) return;
          const item = gallery.querySelectorAll('[data-media-id]')[Number(dot.getAttribute('data-gallery-dot'))];
          if (item) this.#scrollTo(gallery, item, false);
        },
        opts
      );
    }

    // Deep link (?variant=) on phones: open the gallery on that colour without animation.
    const initialVariant = new URL(location.href).searchParams.get('variant');
    if (initialVariant) {
      requestAnimationFrame(() => this.#show(initialVariant, { initial: true }));
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#highlightTimer);
  }

  #onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    let variantId = '';
    if (target instanceof HTMLSelectElement) {
      variantId = target.selectedOptions[0]?.dataset.variantId || '';
    } else {
      variantId = target.dataset.variantId || '';
    }
    if (variantId) this.#show(variantId, { initial: false });
  }

  #syncDots(gallery, dots) {
    const width = gallery.clientWidth || 1;
    const index = Math.min(dots.length - 1, Math.max(0, Math.round(gallery.scrollLeft / width)));
    dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
  }

  #scrollTo(gallery, item, initial) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const left = item.getBoundingClientRect().left - gallery.getBoundingClientRect().left + gallery.scrollLeft;
    gallery.scrollTo({ left, behavior: initial || reduceMotion ? 'auto' : 'smooth' });
  }

  #show(variantId, { initial }) {
    const mediaId = this.#map[String(variantId)];
    if (!mediaId) return;
    const gallery = this.querySelector('[data-product-gallery]');
    const item = gallery?.querySelector(`[data-media-id="${CSS.escape(String(mediaId))}"]`);
    if (!gallery || !item) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = initial || reduceMotion ? 'auto' : 'smooth';
    const isScroller = gallery.scrollWidth > gallery.clientWidth + 1 && getComputedStyle(gallery).overflowX !== 'visible';

    if (isScroller) {
      const left = item.getBoundingClientRect().left - gallery.getBoundingClientRect().left + gallery.scrollLeft;
      gallery.scrollTo({ left, behavior });
      return;
    }

    if (initial) return;
    const rect = item.getBoundingClientRect();
    const fullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (!fullyVisible) {
      const top = rect.top + window.scrollY - 24;
      window.scrollTo({ top: Math.max(0, top), behavior });
    }
    item.setAttribute('data-variant-highlight', '');
    clearTimeout(this.#highlightTimer);
    this.#highlightTimer = setTimeout(() => item.removeAttribute('data-variant-highlight'), 1400);
  }
}

if (!customElements.get('s-variant-gallery')) {
  customElements.define('s-variant-gallery', VariantGallery);
}
